import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ProjectCard from './ProjectCard';
import projectsData from '../../data/projects';

const ProjectsSection = styled.section`
  background-color: ${props => props.theme.colors.offWhite};
  padding: 5rem 0;
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.darkGray};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const SearchContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: ${props => props.theme.fontSizes.medium};
  transition: all ${props => props.theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(61, 90, 254, 0.2);
  }
`;

const FilterContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background-color: ${props => props.active 
    ? props.theme.colors.darkGray 
    : props.theme.colors.lightGray};
  color: ${props => props.active 
    ? props.theme.colors.white 
    : props.theme.colors.darkGray};
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.short};
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.active 
      ? props.theme.colors.darkGray 
      : props.theme.colors.mediumGray};
    color: ${props => props.theme.colors.white};
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
  
  &:hover > *:not(:hover) {
    filter: blur(10px);
    transform: scale(0.9, 0.9);
  }
`;

// Get all unique tags from projects
const allTags = ['All', ...new Set(projectsData.flatMap(project => project.tags))];

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProjects, setFilteredProjects] = useState(projectsData);
  
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  useEffect(() => {
    // Filter projects based on both tag filter and search query
    let filtered = projectsData;
    
    // Apply tag filter
    if (activeFilter !== 'All') {
      filtered = filtered.filter(project => 
        project.tags.includes(activeFilter)
      );
    }
    
    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(project => 
        project.title.toLowerCase().includes(query) ||
        project.description.toLowerCase().includes(query) ||
        project.fullDescription.toLowerCase().includes(query) ||
        project.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    setFilteredProjects(filtered);
  }, [activeFilter, searchQuery]);
  
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  return (
    <ProjectsSection id="projects" ref={ref}>
      <SectionContainer>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          Our Projects
        </SectionTitle>
        
        <SearchContainer
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <SearchInput 
            type="text" 
            placeholder="Search projects by name, description, or technology..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchContainer>
        
        <FilterContainer
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {allTags.map(tag => (
            <FilterButton
              key={tag}
              active={activeFilter === tag}
              onClick={() => handleFilterClick(tag)}
            >
              {tag}
            </FilterButton>
          ))}
        </FilterContainer>
        
        <ProjectsGrid
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <AnimatePresence>
            {filteredProjects.length > 0 ? (
              filteredProjects.map(project => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ 
                  gridColumn: '1 / -1', 
                  textAlign: 'center', 
                  padding: '2rem',
                  color: '#666'
                }}
              >
                No projects match your search criteria. Try adjusting your filters.
              </motion.div>
            )}
          </AnimatePresence>
        </ProjectsGrid>
      </SectionContainer>
    </ProjectsSection>
  );
};

export default Projects; 